import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../../../services';
import { utils } from '../../../utils';
import { ApiResponse } from '../../../types/api';
import ViewLayout from '../../../components/Common/ViewLayout';

interface DriverDetail {
  id: number;
  empresa_id?: number;
  nome: string;
  cpf: string;
  rg?: string;
  data_nascimento?: string;
  telefone?: string;
  celular: string;
  lid?: string;
  email?: string;
  endereco_rua?: string;
  endereco_numero?: string;
  endereco_complemento?: string;
  endereco_bairro?: string;
  endereco_cidade?: string;
  endereco_estado?: string;
  endereco_cep?: string;
  cnh_numero: string;
  cnh_categoria?: string;
  cnh_vencimento: string;
  cnh_primeira_habilitacao?: string;
  cnh_pontos_atuais?: number;
  foto_motorista?: string;
  foto_cnh_frente?: string;
  foto_cnh_verso?: string;
  contato_emergencia_nome?: string;
  contato_emergencia_telefone?: string;
  contato_emergencia_parentesco?: string;
  data_admissao?: string;
  data_demissao?: string;
  salario?: number;
  comissao_percentual?: number;
  banco?: string;
  agencia?: string;
  conta?: string;
  pix_chave?: string;
  observacoes?: string;
  status: string;
  avaliacao_comportamental?: string;
  numero_viagens_realizadas?: number;
  quilometragem_total?: number;
  criado_em: string;
  atualizado_em: string;
  criado_por?: number;
  atualizado_por?: number;
}

const DriverView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [driver, setDriver] = useState<DriverDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDriver();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDriver = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await apiService.getDriver(Number(id)) as ApiResponse<any>;
      
      if (response.success && response.data) {
        setDriver(response.data);
      } else {
        throw new Error(response.message || 'Erro ao carregar motorista');
      }
    } catch (error) {
      console.error('Erro ao carregar motorista:', error);
      setError('Erro ao carregar detalhes do motorista');
      utils.showNotification('Erro ao carregar detalhes do motorista', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const formatDateTime = (dateString: string): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const formatPhone = (phone: string | undefined): string => {
    if (!phone) return '-';
    const cleaned = phone.replace(/\D/g, '');
    
    if (cleaned.length === 11) {
      return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    } else if (cleaned.length === 10) {
      return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }
    return phone;
  };

  const formatCPF = (cpf: string | undefined): string => {
    if (!cpf) return '-';
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const getStatusClass = (status: string): string => {
    const statusMap: Record<string, string> = {
      'ativo': 'status-active',
      'inativo': 'status-inactive'
    };
    return statusMap[status] || 'status-pending';
  };

  const getStatusText = (status: string): string => {
    const statusMap: Record<string, string> = {
      'ativo': 'Ativo',
      'inativo': 'Inativo'
    };
    return statusMap[status] || status;
  };

  const getCNHStatus = (vencimento: string | undefined): { status: string; class: string; text: string } => {
    if (!vencimento) return { status: 'unknown', class: 'status-warning', text: 'Data não informada' };
    
    const validityDate = new Date(vencimento);
    const today = new Date();
    const diffTime = validityDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return { status: 'expired', class: 'status-danger', text: 'CNH Vencida' };
    } else if (diffDays <= 30) {
      return { status: 'warning', class: 'status-warning', text: 'Vence em breve' };
    } else {
      return { status: 'valid', class: 'status-success', text: 'CNH Válida' };
    }
  };

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <button className="btn btn-secondary" onClick={() => navigate('/motoristas')}>
            <i className="fas fa-arrow-left"></i>
            Voltar
          </button>
          <h1>Carregando Motorista...</h1>
        </div>
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <div className="loading-spinner-large"></div>
          <p>Carregando detalhes do motorista...</p>
        </div>
      </div>
    );
  }

  if (error || !driver) {
    return (
      <div className="page-container">
        <div className="page-header">
          <button className="btn btn-secondary" onClick={() => navigate('/motoristas')}>
            <i className="fas fa-arrow-left"></i>
            Voltar
          </button>
          <h1>Erro ao Carregar</h1>
        </div>
        <div className="page-content">
          <div className="card">
            <div className="card-body" style={{ textAlign: 'center', padding: '50px 20px' }}>
              <i className="fas fa-exclamation-triangle" style={{ fontSize: '3rem', color: '#e74c3c', marginBottom: '20px' }}></i>
              <h3>{error || 'Motorista não encontrado'}</h3>
              <p>O motorista solicitado não pôde ser carregado.</p>
              <button className="btn btn-primary" onClick={() => navigate('/motoristas')}>
                Voltar para Lista
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const cnhStatus = getCNHStatus(driver.cnh_vencimento);

  return (
    <ViewLayout
      title="Detalhes do Motorista"
      subtitle={driver.nome}
      entityId={driver.id}
      isLoading={isLoading}
      editUrl={`/motoristas/editar/${driver.id}`}
      onBack={() => navigate('/motoristas')}
    >
      {/* Informações Básicas */}
      <div style={{ marginBottom: '30px' }}>
        <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
          Informações Básicas
        </h3>
        
        <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
          <div className="info-item">
            <label className="form-label">Nome Completo</label>
            <span className="info-value">{driver.nome}</span>
          </div>

          <div className="info-item">
            <label className="form-label">CPF</label>
            <span className="info-value">{formatCPF(driver.cpf)}</span>
          </div>

          <div className="info-item">
            <label className="form-label">Celular</label>
            <span className="info-value">{formatPhone(driver.celular)}</span>
          </div>

          <div className="info-item">
            <label className="form-label">LID (WhatsApp)</label>
            <span className="info-value">{driver.lid || '-'}</span>
          </div>

          <div className="info-item">
            <label className="form-label">Status</label>
            <span className="info-value">
              <span className={`status-badge ${getStatusClass(driver.status)}`}>
                {getStatusText(driver.status)}
              </span>
            </span>
          </div>
        </div>
      </div>

      {/* Informações Pessoais */}
      <div style={{ marginBottom: '30px' }}>
        <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
          Informações Pessoais
        </h3>
        
        <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
          <div className="info-item">
            <label className="form-label">RG</label>
            <span className="info-value">{driver.rg || '-'}</span>
          </div>

          <div className="info-item">
            <label className="form-label">Data de Nascimento</label>
            <span className="info-value">{formatDate(driver.data_nascimento)}</span>
          </div>

          <div className="info-item">
            <label className="form-label">Telefone</label>
            <span className="info-value">{formatPhone(driver.telefone)}</span>
          </div>

          <div className="info-item">
            <label className="form-label">Email</label>
            <span className="info-value">{driver.email || '-'}</span>
          </div>
        </div>
      </div>

      {/* Endereço */}
      <div style={{ marginBottom: '30px' }}>
        <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
          Endereço
        </h3>
        
        <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
          <div className="info-item">
            <label className="form-label">Rua</label>
            <span className="info-value">{driver.endereco_rua || '-'}</span>
          </div>

          <div className="info-item">
            <label className="form-label">Número</label>
            <span className="info-value">{driver.endereco_numero || '-'}</span>
          </div>

          <div className="info-item">
            <label className="form-label">Complemento</label>
            <span className="info-value">{driver.endereco_complemento || '-'}</span>
          </div>

          <div className="info-item">
            <label className="form-label">Bairro</label>
            <span className="info-value">{driver.endereco_bairro || '-'}</span>
          </div>

          <div className="info-item">
            <label className="form-label">Cidade</label>
            <span className="info-value">{driver.endereco_cidade || '-'}</span>
          </div>

          <div className="info-item">
            <label className="form-label">Estado</label>
            <span className="info-value">{driver.endereco_estado || '-'}</span>
          </div>

          <div className="info-item">
            <label className="form-label">CEP</label>
            <span className="info-value">{driver.endereco_cep || '-'}</span>
          </div>
        </div>
      </div>

      {/* Documentação - CNH */}
      <div style={{ marginBottom: '30px' }}>
        <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
          Documentação - CNH
        </h3>
        
        <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
          <div className="info-item">
            <label className="form-label">Número da CNH</label>
            <span className="info-value">{driver.cnh_numero}</span>
          </div>

          <div className="info-item">
            <label className="form-label">Categoria</label>
            <span className="info-value">{driver.cnh_categoria || '-'}</span>
          </div>

          <div className="info-item">
            <label className="form-label">Validade CNH</label>
            <span className="info-value">{formatDate(driver.cnh_vencimento)}</span>
          </div>

          <div className="info-item">
            <label className="form-label">Primeira Habilitação</label>
            <span className="info-value">{formatDate(driver.cnh_primeira_habilitacao)}</span>
          </div>

          <div className="info-item">
            <label className="form-label">Pontos Atuais</label>
            <span className="info-value">{driver.cnh_pontos_atuais || 0}</span>
          </div>

          <div className="info-item">
            <label className="form-label">Status da CNH</label>
            <span className="info-value">
              <span className={`status-badge ${cnhStatus.class}`}>
                {cnhStatus.text}
              </span>
            </span>
          </div>
        </div>
      </div>

      {/* Contato de Emergência */}
      <div style={{ marginBottom: '30px' }}>
        <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
          Contato de Emergência
        </h3>
        
        <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
          <div className="info-item">
            <label className="form-label">Nome</label>
            <span className="info-value">{driver.contato_emergencia_nome || '-'}</span>
          </div>

          <div className="info-item">
            <label className="form-label">Telefone</label>
            <span className="info-value">{formatPhone(driver.contato_emergencia_telefone)}</span>
          </div>

          <div className="info-item">
            <label className="form-label">Parentesco</label>
            <span className="info-value">{driver.contato_emergencia_parentesco || '-'}</span>
          </div>
        </div>
      </div>

      {/* Dados Profissionais */}
      <div style={{ marginBottom: '30px' }}>
        <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
          Dados Profissionais
        </h3>
        
        <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
          <div className="info-item">
            <label className="form-label">Data de Admissão</label>
            <span className="info-value">{formatDate(driver.data_admissao)}</span>
          </div>

          <div className="info-item">
            <label className="form-label">Data de Demissão</label>
            <span className="info-value">{formatDate(driver.data_demissao)}</span>
          </div>

          <div className="info-item">
            <label className="form-label">Salário</label>
            <span className="info-value">
              {driver.salario ? `R$ ${parseFloat(driver.salario as any).toFixed(2)}` : '-'}
            </span>
          </div>

          <div className="info-item">
            <label className="form-label">Comissão (%)</label>
            <span className="info-value">
              {driver.comissao_percentual ? `${driver.comissao_percentual}%` : '-'}
            </span>
          </div>

          <div className="info-item">
            <label className="form-label">Banco</label>
            <span className="info-value">{driver.banco || '-'}</span>
          </div>

          <div className="info-item">
            <label className="form-label">Agência</label>
            <span className="info-value">{driver.agencia || '-'}</span>
          </div>

          <div className="info-item">
            <label className="form-label">Conta</label>
            <span className="info-value">{driver.conta || '-'}</span>
          </div>

          <div className="info-item">
            <label className="form-label">Chave PIX</label>
            <span className="info-value">{driver.pix_chave || '-'}</span>
          </div>
        </div>
      </div>

      {/* Estatísticas */}
      <div style={{ marginBottom: '30px' }}>
        <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
          Estatísticas
        </h3>
        
        <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
          <div className="info-item">
            <label className="form-label">Viagens Realizadas</label>
            <span className="info-value">{driver.numero_viagens_realizadas || 0}</span>
          </div>

          <div className="info-item">
            <label className="form-label">Quilometragem Total</label>
            <span className="info-value">
              {driver.quilometragem_total ? `${parseFloat(driver.quilometragem_total as any).toFixed(2)} km` : '0 km'}
            </span>
          </div>
        </div>
      </div>

      {/* Avaliação Comportamental */}
      {driver.avaliacao_comportamental && (
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            Avaliação Comportamental
          </h3>
          
          <div className="info-item">
            <label className="form-label">Avaliação</label>
            <div className="info-value" style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
              {driver.avaliacao_comportamental}
            </div>
          </div>
        </div>
      )}

      {/* Observações */}
      {driver.observacoes && (
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            Observações
          </h3>
          
          <div className="info-item">
            <label className="form-label">Observações</label>
            <div className="info-value" style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
              {driver.observacoes}
            </div>
          </div>
        </div>
      )}

      {/* Informações de Auditoria */}
      <div style={{ marginBottom: '30px' }}>
        <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
          Informações de Auditoria
        </h3>
        
        <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
          <div className="info-item">
            <label className="form-label">Criado em</label>
            <span className="info-value">{formatDateTime(driver.criado_em)}</span>
          </div>

          <div className="info-item">
            <label className="form-label">Atualizado em</label>
            <span className="info-value">{formatDateTime(driver.atualizado_em)}</span>
          </div>
        </div>
      </div>
      {/* Acesso PWA */}
      <div style={{ marginBottom: '30px' }}>
        <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
          Acesso PWA (App Motorista)
        </h3>
        <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '20px' }}>
          <p style={{ color: '#7f8c8d', marginBottom: '15px' }}>
            Defina um PIN para que o motorista acesse o app pelo celular em <strong>/motorista/login</strong>
          </p>
          <PinForm motoristaId={driver.id} />
        </div>
      </div>
    </ViewLayout>
  );
};

const PinForm: React.FC<{ motoristaId: number }> = ({ motoristaId }) => {
  const [pin, setPin] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleDefinirPin = async () => {
    if (pin.length < 4) { utils.showNotification('PIN deve ter pelo menos 4 digitos', { type: 'error' }); return; }
    if (!/^\d+$/.test(pin)) { utils.showNotification('PIN deve conter apenas numeros', { type: 'error' }); return; }

    setIsLoading(true);
    try {
      const response = await apiService.request<any>(
        `${(await import('../../../services/config')).Config.API_BASE_URL}/motoristas/${motoristaId}/definir-pin`,
        { method: 'POST', body: JSON.stringify({ pin, pin_temporario: true }) }
      );
      if (response.success) {
        utils.showNotification('PIN definido com sucesso! O motorista pode acessar o app.', { type: 'success' });
        setPin('');
      } else {
        utils.showNotification(response.message || 'Erro ao definir PIN', { type: 'error' });
      }
    } catch (err: any) {
      utils.showNotification(err.message || 'Erro ao definir PIN', { type: 'error' });
    } finally { setIsLoading(false); }
  };

  return (
    <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
      <div style={{ flex: 1 }}>
        <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '0.9rem', color: '#2c3e50' }}>Novo PIN (4-8 digitos)</label>
        <input
          type="text"
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
          placeholder="Ex: 1234"
          className="form-control"
          inputMode="numeric"
          maxLength={8}
        />
      </div>
      <button
        onClick={handleDefinirPin}
        disabled={isLoading || pin.length < 4}
        className="btn btn-primary"
        style={{ whiteSpace: 'nowrap' }}
      >
        {isLoading ? 'Salvando...' : 'Definir PIN'}
      </button>
    </div>
  );
};

export default DriverView;
